import { JobStatus, Job, statusToDbFormat } from '@applicator/shared'

import { supabase } from '../../lib/supabase'
import { DateMode } from '../JobDateChips/types'

import { formatDateTime } from './utils'

interface StatusHandlersParams {
  jobs: Job[]
  setJobs: React.Dispatch<React.SetStateAction<Job[]>>
  setError: React.Dispatch<React.SetStateAction<string | null>>
}

export const createStatusHandlers = ({
  jobs,
  setJobs,
  setError,
}: StatusHandlersParams) => {
  const handleStatusChange = async (rowIndex: number, newStatus: JobStatus) => {
    const job = jobs[rowIndex]
    if (!job.id) {
      // Fallback for jobs without id
      const updatedJobs = [...jobs]
      updatedJobs[rowIndex] = { ...updatedJobs[rowIndex], status: newStatus }
      setJobs(updatedJobs)
      return
    }

    // Если статус меняется на "Applied" и Date Applied еще не установлена или была удалена (cleared),
    // автоматически устанавливаем текущую дату
    const updateData: Record<string, unknown> = {
      status: statusToDbFormat(newStatus),
    }

    if (newStatus === 'Applied') {
      // Загружаем текущие данные job из Supabase для проверки режима и даты
      const { data: currentJobData } = await supabase
        .from('jobs')
        .select('applied_at, date_applied_mode')
        .eq('id', job.id)
        .single()

      const dateAppliedMode = currentJobData?.date_applied_mode as DateMode | undefined
      const appliedAt = currentJobData?.applied_at
      
      // Проверяем, нужно ли устанавливать дату:
      // 1. Если applied_at null или пустой
      // 2. Если режим 'cleared'
      // 3. Если в UI отображается "Not set" или "N/A"
      const needsDateSet = !appliedAt || 
                          dateAppliedMode === 'cleared' || 
                          !job.dateApplied || 
                          job.dateApplied === 'Not set' || 
                          job.dateApplied === 'N/A'
      
      if (needsDateSet) {
        // Use NOW() for TIMESTAMPTZ (includes date and time)
        updateData.applied_at = new Date().toISOString()
        updateData.date_applied_mode = 'manual'
        // For manual mode, store only date part (YYYY-MM-DD)
        const today = new Date()
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
        updateData.date_applied_manual = todayStr
      }
    }

    // Optimistic update
    const updatedJobs = [...jobs]
    updatedJobs[rowIndex] = { ...updatedJobs[rowIndex], status: newStatus }
    if (newStatus === 'Applied' && updateData.applied_at) {
      // Format date and time for display
      updatedJobs[rowIndex].dateApplied = formatDateTime(updateData.applied_at)
    }
    setJobs(updatedJobs)

    // Update in Supabase
    const { error } = await supabase
      .from('jobs')
      .update(updateData)
      .eq('id', job.id)

    if (error) {
      // Revert on error
      setJobs(jobs)
      setError(error.message)
    }
  }

  const handleDeleteJob = (rowIndex: number) => {
    const job = jobs[rowIndex]
    if (!job.id) {
      // Fallback for jobs without id
      const updatedJobs = jobs.filter((_, idx) => idx !== rowIndex)
      setJobs(updatedJobs)
      return null
    }

    // Return delete confirmation data - actual deletion is handled by confirmDeleteJob
    return {
      rowIndex,
      jobIds: [job.id],
      message: `Are you sure you want to delete "${job.position}" at ${job.company}?`,
    }
  }

  const handleExcitementChange = async (rowIndex: number, newValue: number) => {
    const job = jobs[rowIndex]
    if (!job.id) {
      // Fallback for jobs without id
      const updatedJobs = [...jobs]
      updatedJobs[rowIndex] = {
        ...updatedJobs[rowIndex],
        excitement: newValue,
      }
      setJobs(updatedJobs)
      return
    }

    // Optimistic update
    const updatedJobs = [...jobs]
    updatedJobs[rowIndex] = {
      ...updatedJobs[rowIndex],
      excitement: newValue,
    }
    setJobs(updatedJobs)

    // Update in Supabase
    const { error } = await supabase
      .from('jobs')
      .update({ excitement: newValue })
      .eq('id', job.id)

    if (error) {
      // Revert on error
      setJobs(jobs)
      setError(error.message)
    }
  }

  return {
    handleStatusChange,
    handleDeleteJob,
    handleExcitementChange,
  }
}
